import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Post } from '@/mocks/posts';
import { categories } from '@/mocks/categories';
import { getPostPublishDate, formatPublishDate } from '@/utils/scheduler';

interface RelatedPanelProps {
  posts: Post[];
  currentSlug: string;
}

export default function RelatedPanel({ posts, currentSlug }: RelatedPanelProps) {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [triggered, setTriggered] = useState(false);
  const articleRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Reset on article change
    setVisible(false);
    setDismissed(false);
    setTriggered(false);
  }, [currentSlug]);

  useEffect(() => {
    if (dismissed) return;

    const handleScroll = () => {
      if (triggered) return;

      // Find the article element
      const article = document.querySelector('article');
      if (!article) return;
      articleRef.current = article;

      const rect = article.getBoundingClientRect();
      const articleHeight = article.offsetHeight;
      const scrolledIntoArticle = -rect.top;
      const progress = scrolledIntoArticle / articleHeight;

      if (progress >= 0.75) {
        setTriggered(true);
        setVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [triggered, dismissed]);

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
  };

  if (posts.length === 0 || dismissed) return null;

  return (
    <div
      className={`fixed bottom-6 right-6 z-40 w-80 transition-all duration-500 ease-out ${
        visible
          ? 'opacity-100 translate-y-0 pointer-events-auto'
          : 'opacity-0 translate-y-8 pointer-events-none'
      }`}
    >
      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-700 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-stone-50 dark:bg-stone-800 border-b border-stone-100 dark:border-stone-700">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center text-rose-500">
              <i className="ri-bookmark-line text-sm" />
            </div>
            <span className="text-xs font-semibold text-stone-700 dark:text-stone-200 uppercase tracking-widest">
              Keep Reading
            </span>
          </div>
          <button
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="w-6 h-6 flex items-center justify-center text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 cursor-pointer rounded-md hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors"
          >
            <i className="ri-close-line text-sm" />
          </button>
        </div>

        {/* Articles list */}
        <div className="divide-y divide-stone-100 dark:divide-stone-800">
          {posts.slice(0, 3).map((post) => (
            <RelatedPanelCard key={post.id} post={post} onNavigate={handleDismiss} />
          ))}
        </div>

        {/* Footer link */}
        <div className="px-4 py-2.5 bg-stone-50 dark:bg-stone-800 border-t border-stone-100 dark:border-stone-700">
          <Link
            to="/archive"
            onClick={handleDismiss}
            className="text-xs text-rose-500 hover:text-rose-600 dark:hover:text-rose-400 font-medium flex items-center gap-1 cursor-pointer"
          >
            <i className="ri-archive-line text-xs" />
            Browse all articles
          </Link>
        </div>
      </div>
    </div>
  );
}

function RelatedPanelCard({ post, onNavigate }: { post: Post; onNavigate: () => void }) {
  const cat = categories.find((c) => c.slug === post.category);
  const publishDate = getPostPublishDate(post);

  return (
    <Link
      to={`/post/${post.slug}`}
      onClick={onNavigate}
      className="group flex gap-3 px-4 py-3 hover:bg-rose-50 dark:hover:bg-stone-800/80 transition-colors cursor-pointer"
    >
      {/* Thumbnail */}
      <div className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
        />
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        {cat && (
          <span
            className="text-xs font-semibold uppercase tracking-wider"
            style={{ color: cat.color }}
          >
            {cat.name}
          </span>
        )}
        <h4 className="text-xs font-semibold text-stone-800 dark:text-stone-100 leading-snug mt-0.5 group-hover:text-rose-600 dark:group-hover:text-rose-400 transition-colors line-clamp-2">
          {post.title}
        </h4>
        <div className="flex items-center gap-2 mt-1 text-xs text-stone-400 dark:text-stone-500">
          <span className="flex items-center gap-0.5 whitespace-nowrap">
            <i className="ri-time-line text-xs" />
            {post.readTime} min
          </span>
          <span className="whitespace-nowrap hidden sm:inline">
            {formatPublishDate(publishDate)}
          </span>
        </div>
      </div>
    </Link>
  );
}
